/**
 * Retail Cloud Connect — search event tracking for Boost AI Search & Discovery.
 *
 * Sends "search" user events to Nimstrata's Storefront API from the search
 * results page (/search?q=...) only — collections are not tracked.
 * Docs: https://nimstrata.com/docs/shopify/api-access/
 *
 * Events fire on the initial (SSR) render and again whenever Boost updates
 * the results client-side ("Lataa lisää" pagination, sorting, new query).
 * Product IDs are read from the rendered grid in display order.
 */
(function () {
  'use strict';

  var CONFIG = {
    // GCP project ID for this store (RCC app -> Google Cloud page).
    projectName: 'laatukoru-155-eur',
    eventEndpoint: 'https://storefront.retailconnect.app/v1/event',
    // Boost search page size ("limit" on its bc-sf-filter/search requests).
    productsPerPage: 48,
    // e.g. ['CONTROL'] when running experiments in the Google Cloud Console.
    experimentIds: ['CONTROL']
  };

  // Search results page only (optionally locale-prefixed), never collections.
  if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(window.location.pathname)) return;

  var VISITOR_COOKIE = 'rcc-visitor-id';

  function getCookie(name) {
    var match = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return match ? decodeURIComponent(match[1]) : null;
  }

  function createUUID() {
    var s = [];
    var hexDigits = '0123456789abcdef';
    for (var i = 0; i < 36; i++) {
      s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = '4';
    s[19] = hexDigits.substr((s[19].charCodeAt(0) & 0x3) | 0x8, 1);
    s[8] = s[13] = s[18] = s[23] = '-';
    return s.join('');
  }

  function getVisitorId() {
    var id = getCookie(VISITOR_COOKIE);
    if (id) return id;
    id = createUUID();
    document.cookie = VISITOR_COOKIE + '=' + id + '; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT; SameSite=Lax';
    return id;
  }

  function getCustomerId() {
    try {
      return (window.__st && window.__st.cid) ||
        (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
         window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId) ||
        null;
    } catch (e) {
      return null;
    }
  }

  // Product IDs in display order, scoped to the Boost search-page grid so the
  // predictive-search popup and any recommendation widgets are never counted.
  function getDisplayedProductIds() {
    var items = document.querySelectorAll(
      '.boost-sd-container-search-page .boost-sd__product-list .boost-sd__product-item[data-product-id]'
    );
    if (!items.length) {
      items = document.querySelectorAll('.boost-sd__product-list .boost-sd__product-item[data-product-id]');
    }
    var ids = [];
    var seen = {};
    for (var i = 0; i < items.length; i++) {
      var id = items[i].getAttribute('data-product-id');
      if (id && !seen[id]) {
        seen[id] = true;
        ids.push(id);
      }
    }
    return ids;
  }

  function sendEvent(searchQuery, offset, productIds) {
    var userEvent = {
      eventType: 'search',
      visitorId: getVisitorId(),
      searchQuery: searchQuery,
      offset: offset,
      productDetails: productIds.map(function (id) {
        return { product: { id: String(id) } };
      }),
      userInfo: { userAgent: navigator.userAgent }
    };
    var customerId = getCustomerId();
    if (customerId) userEvent.userInfo.userId = String(customerId);
    if (CONFIG.experimentIds.length) userEvent.experimentIds = CONFIG.experimentIds;

    try {
      window.fetch(CONFIG.eventEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectName: CONFIG.projectName, userEvent: userEvent }),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* never break the storefront */ }
  }

  // "Lataa lisää" appends to the grid, so track which IDs were already
  // reported and only send the newly rendered page of products.
  var reportedIds = {};
  var lastSignature = '';

  function collect() {
    var params = new URLSearchParams(window.location.search);
    var query = (params.get('q') || '').trim();
    if (!query) return; // empty query would be a browse event — out of scope

    var ids = getDisplayedProductIds();
    if (!ids.length) return;

    var page = parseInt(params.get('page'), 10) || 1;
    var signature = query + '|' + page + '|' + ids.join(',');
    if (signature === lastSignature) return;

    var newIds, offset;
    if (page === 1) {
      // Fresh result set (new query, re-sort, first page): report everything.
      reportedIds = {};
      newIds = ids;
      offset = 0;
    } else {
      newIds = ids.filter(function (id) { return !reportedIds[id]; });
      offset = (page - 1) * CONFIG.productsPerPage;
    }
    if (!newIds.length) return;

    lastSignature = signature;
    newIds.forEach(function (id) { reportedIds[id] = true; });
    sendEvent(query, offset, newIds);
  }

  // Boost re-renders shortly after its API responds; poll briefly since the
  // exact render timing isn't observable. collect() is idempotent.
  var pollTimers = [];
  function scheduleCollect() {
    pollTimers.forEach(clearTimeout);
    pollTimers = [200, 500, 1000, 2000, 3500].map(function (ms) {
      return setTimeout(collect, ms);
    });
  }

  // Client-side updates (load more / sort / new query) all go through
  // Boost's bc-sf-filter/search endpoint.
  var originalFetch = window.fetch;
  window.fetch = function () {
    var promise = originalFetch.apply(this, arguments);
    try {
      var input = arguments[0];
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/bc-sf-filter\/search\?/.test(url)) {
        promise.then(scheduleCollect, function () {});
      }
    } catch (e) { /* ignore */ }
    return promise;
  };

  window.addEventListener('popstate', scheduleCollect);

  // Initial SSR render (retries cover Boost's client-render fallback).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCollect);
  } else {
    scheduleCollect();
  }
})();
